import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { Alert } from 'antd';

import { pmsEndpoints } from '~settings/endpoints';
import {
  DocumentPdf,
  DocumentNotUploaded,
  CheckCircle,
  IconEye,
  ClockCircle,
  WarningLarge,
} from 'common/components/svgs';

const useStyles = makeStyles(theme => ({
  document: {
    display: 'flex',
    marginTop: 16,
  },
  file: {
    marginRight: 24,
  },
  fileContent: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    width: 200,
    height: 200,
    lineHeight: '200px',
    textAlign: 'center',
    marginBottom: 8,
    border: `1px solid ${theme.colors.gray5}`,
    backgroundColor: theme.colors.gray2,
    overflow: 'hidden',
    borderRadius: 2,
  },
  fileContentHover: {
    '&:hover > div': {
      opacity: 1,
    },
  },
  fileOptions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    cursor: 'pointer',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  filePreview: {
    display: 'flex',
    alignItems: 'center',

    '& > span': {
      ...theme.typography.body2,
      color: theme.colors.gray1,
      paddingLeft: 4,
    },
  },
  fileImg: {
    width: '100%',
    height: 'auto',
  },
  filePdf: {
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
  fileNotUploaded: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
  },
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 0,
  },
  checkIcon: {
    width: 14,
    height: 14,
  },
  fileLabelText: {
    paddingLeft: 9,
  },
  fileDisabledText: {
    paddingLeft: 9,
    color: theme.colors.disableColor,
  },
  warning: {
    '& .ant-alert': {
      padding: '6px 0 6px 38px',
      border: 'none',
    },

    '& .ant-alert-message': {
      ...theme.typography.body4,
      color: theme.colors.orange6,
      marginBottom: 0,
    },

    '& .ant-alert-description': {
      ...theme.typography.body4,
      color: theme.colors.captionColor,
    },
  },
  warnIcon: {
    width: 16,
    height: 16,
    top: '9px !important',
    left: '12px !important',
  },
  fileWarningIcon: {
    width: 16,
    height: 16,
    fill: theme.colors.disableColor,
  },
}));

const DocumentInfo = ({ t, property, files }) => {
  const classes = useStyles();

  const filePath = pmsEndpoints.getDocumentFile.url();

  const documentations = property?.documentations
    .filter(item => item.check)
    ?.map(item => {
      const file = files.find(_ => _.name.startsWith(item.slug));

      if (file) {
        return Object.assign(item, {
          file,
        });
      }

      return item;
    });

  const handlePreviewClick = file => {
    window.open(`${filePath}/${file.source}`);
  };

  return (
    <If condition={documentations?.length > 0}>
      <If condition={files.length !== documentations.length}>
        <div className={classes.warning}>
          <Alert
            type="warning"
            showIcon={true}
            icon={<ClockCircle className={classes.warnIcon} />}
            message={t('landlord.long_tail.booking_details.label.document_info.message')}
            description={t('landlord.long_tail.booking_details.label.document_info.description')}
          />
        </div>
      </If>

      <div className={classes.document}>
        <For each="document" of={documentations}>
          <div className={classes.file}>
            <div className={`${classes.fileContent} ${document.file && classes.fileContentHover}`}>
              <Choose>
                <When condition={document.file}>
                  {document.file?.type?.includes('image') ? (
                    <img className={classes.fileImg} src={`${filePath}/${document.file.source}`} />
                  ) : (
                    <DocumentPdf className={classes.filePdf} />
                  )}

                  <div
                    className={classes.fileOptions}
                    onClick={() => handlePreviewClick(document.file)}
                  >
                    <div className={classes.filePreview}>
                      <IconEye />
                      <span>
                        {t('landlord.long_tail.booking_details.label.document_info.preview')}
                      </span>
                    </div>
                  </div>
                </When>
                <Otherwise>
                  <DocumentNotUploaded className={classes.fileNotUploaded} />
                </Otherwise>
              </Choose>
            </div>
            <p className={classes.fileLabel}>
              {document.file ? (
                <CheckCircle className={classes.checkIcon} />
              ) : (
                <WarningLarge className={classes.fileWarningIcon} />
              )}
              <span className={document.file ? classes.fileLabelText : classes.fileDisabledText}>
                {t(`landlord.long_tail.booking_details.label.document_info.${document.slug}`)}
              </span>
            </p>
          </div>
        </For>
      </div>
    </If>
  );
};

export default DocumentInfo;
